import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPencil, LuTrash, LuPlus, LuWarehouse } from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import { FilterData } from "./components";
import { formatPhoneInternational } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    name: "",
    address: "",
    contact: "",
};

const WarehouseData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("warehouse.edit") || hasPermission("warehouse.delete");
    const table = useDataTable(
        "warehouses/data",
        "warehouses/{id}/delete",
        {},
        initialFilters,
    );

    // Filter labels
    const filterLabels = {
        name: { label: "Nama Gudang" },
        address: { label: "Alamat" },
        contact: { label: "Kontak" },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data gudang. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama Gudang", skeletonWidth: "w-48" },
        { name: "Alamat", skeletonWidth: "w-64" },
        { name: "Kontak", skeletonWidth: "w-32" },
        { name: "Keterangan", skeletonWidth: "w-40" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    const columns = [
        { key: "no", label: "No.", width: 50 },
        { key: "name", label: "Nama Gudang" },
        { key: "address", label: "Alamat" },
        { key: "contact", label: "Kontak" },
        { key: "description", label: "Keterangan" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "name":
                return <ThemedText size="sm">{item.name}</ThemedText>;
            case "address":
                return (
                    <ThemedText
                        size="xs"
                        variant="secondary"
                        className="line-clamp-2"
                    >
                        {item.first_address?.full_address || "-"}
                    </ThemedText>
                );
            case "contact":
                return (
                    <div className="flex flex-col">
                        <ThemedText size="sm" className="whitespace-nowrap">
                            {formatPhoneInternational(
                                item.first_contact_number?.number,
                            )}
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            {item.first_contact_number?.contact_type?.name || ""}
                        </ThemedText>
                    </div>
                );
            case "description":
                return (
                    <ThemedText
                        size="xs"
                        variant="secondary"
                        className="line-clamp-1"
                    >
                        {item.description || "-"}
                    </ThemedText>
                );
            case "actions":
                return (
                    <div className="relative flex items-center justify-end gap-2">
                        {hasPermission("warehouse.edit") && (
                            <Tooltip content="Edit Gudang">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    as={Link}
                                    to={`/warehouse/${item.id}/edit`}
                                >
                                    <LuPencil
                                        size={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission("warehouse.delete") && (
                            <Tooltip content="Hapus Gudang">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    color="danger"
                                    variant="light"
                                    onPress={() =>
                                        table.deleteState.openDeleteModal(
                                            item.id,
                                        )
                                    }
                                >
                                    <LuTrash size={14} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Gudang"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Gudang" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("warehouse.create") && (
                        <Button
                            as={Link}
                            to="/warehouse/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Gudang
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData table={table} initialFilters={initialFilters} />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Gudang"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada gudang yang cocok dengan filter."
                                    : "Mulai dengan menambahkan gudang baru."
                            }
                            icon={LuWarehouse}
                        />
                    ) : (
                        <Table aria-label="Tabel Gudang" shadow="none">
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                        width={column.width}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal
                table={table}
                title="Hapus Gudang"
                description="Apakah Anda yakin ingin menghapus gudang ini? Tindakan ini tidak dapat dibatalkan."
            />
        </div>
    );
};

export default WarehouseData;
